import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: number;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  displayTime: number;
}

export interface PendingConfirm {
  title: string;
  message: string;
  resolve: (value: boolean) => void;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private static _instance: ToastService;
  private nextId = 0;
  readonly toasts = signal<Toast[]>([]);
  readonly pendingConfirm = signal<PendingConfirm | null>(null);

  constructor() {
    ToastService._instance = this;
  }

  /** Used by static AppNotify wrapper to get the singleton */
  static getInstance(): ToastService | null {
    return ToastService._instance ?? null;
  }

  info(message: string, displayTime = 3000) { this.show(message, 'info', displayTime); }
  warning(message: string, displayTime = 3000) { this.show(message, 'warning', displayTime); }
  error(message: string, displayTime = 3000) { this.show(message, 'error', displayTime); }
  success(message: string, displayTime = 3000) { this.show(message, 'success', displayTime); }

  private show(message: string, type: Toast['type'], displayTime: number) {
    const id = this.nextId++;
    const toast: Toast = { id, message, type, displayTime };
    this.toasts.update(t => [...t, toast]);
    setTimeout(() => this.dismiss(id), displayTime);
  }

  dismiss(id: number) {
    this.toasts.update(t => t.filter(x => x.id !== id));
  }

  confirm(message: string, title: string): Promise<boolean> {
    return new Promise(resolve => {
      this.pendingConfirm.set({ title, message, resolve });
    });
  }

  resolveConfirm(value: boolean) {
    const pending = this.pendingConfirm();
    if (pending) {
      pending.resolve(value);
      this.pendingConfirm.set(null);
    }
  }
}
